import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { AlertDialog } from '@base-ui/react/alert-dialog';
import { useTranslation } from 'react-i18next';
import { RiArrowLeftSLine, RiArrowRightSLine, RiCloseLine, RiErrorWarningLine, RiPushpin2Fill, RiPushpin2Line } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { IconChip, NamedIcon } from './icons';
import { rowFocus } from './sidebar-parts';
import { useTabs, useTabsStore, type ShellTab } from './tabs';
import { useMe } from './use-me';

const DRAG_TYPE = 'application/x-atnt-tab';

type Edges = { left: boolean; right: boolean };

/** Which ends of the strip have hidden tabs (shows the scroll arrows). */
function overflowEdges(el: HTMLElement | null, prev: Edges): Edges {
  if (!el) return prev;
  const left = el.scrollLeft > 1;
  const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  return prev.left === left && prev.right === right ? prev : { left, right };
}

const focusTabAt = (list: HTMLElement | null, i: number) => list?.querySelectorAll<HTMLElement>('[role="tab"]')[i]?.focus();

/**
 * Workspace tab strip (h32, 01-design-system §6.3, 317:7074): idle tabs are #1e2b49 pills (r12, 400 14/20 White/60)
 * with a pushpin (18) before the close icon, the active tab is #0f172a with top corners r10 and merges into the page
 * header (pin shown only when pinned). Overflow scrolls horizontally
 * (wheel, arrows, active tab kept in view); middle click closes; drag to reorder; dirty tabs ask before closing.
 */
export function TopTabStrip() {
  const { t } = useTranslation('shell');
  const tabs = useTabsStore((s) => s.tabs);
  const activeKey = useTabsStore((s) => s.activeKey);
  const api = useTabs();
  const me = useMe();
  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<Edges>({ left: false, right: false });
  const [pendingClose, setPendingClose] = useState<ShellTab | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setEdges((e) => overflowEdges(el, e)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // keep the active tab in view (scrolling the strip only) and refresh the overflow arrows when the list changes
  const tabCount = tabs.length;
  useEffect(() => {
    const box = scroller.current;
    const tab = box?.querySelector<HTMLElement>('[data-active]');
    if (box && tab) {
      const b = box.getBoundingClientRect();
      const r = tab.getBoundingClientRect();
      if (r.left < b.left) box.scrollLeft -= b.left - r.left;
      else if (r.right > b.right) box.scrollLeft += r.right - b.right;
    }
    setEdges((e) => overflowEdges(box, e));
  }, [activeKey, tabCount]);

  const requestClose = (tab: ShellTab) => (tab.dirty ? setPendingClose(tab) : api.close(tab.key));

  const tenantName = (slug?: string) => (slug && me.tenants.length > 1 ? me.tenants.find((x) => x.slug === slug)?.name : undefined);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, tab: ShellTab, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      api.activate(tab.key);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      requestClose(tab);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusTabAt(scroller.current, (index + 1) % tabs.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusTabAt(scroller.current, (index - 1 + tabs.length) % tabs.length);
    }
  };

  // the dragged tab takes the drop target's place (after it when moving right, before it when moving left)
  const onDrop = (e: DragEvent<HTMLDivElement>, index: number) => {
    const key = e.dataTransfer.getData(DRAG_TYPE);
    if (!key) return;
    e.preventDefault();
    api.move(key, index);
    setDragKey(null);
  };

  const scrollBy = (dir: -1 | 1) => scroller.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });

  return (
    <div className="relative flex h-32 shrink-0 bg-panel">
      {/* the page header's top border (drawn outside it in Figma): over idle tabs, hidden under the active one */}
      <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 z-1 h-px bg-line-subtle" />
      <div
        ref={scroller}
        role="tablist"
        aria-label={t('tabs.label')}
        onScroll={(e) => {
          const el = e.currentTarget;
          setEdges((prev) => overflowEdges(el, prev));
        }}
        onWheel={(e) => {
          if (scroller.current && Math.abs(e.deltaY) > Math.abs(e.deltaX)) scroller.current.scrollLeft += e.deltaY;
        }}
        className="flex min-w-0 flex-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab, index) => {
          const active = tab.key === activeKey;
          const title = tab.title || (tab.titleKey ? t(tab.titleKey) : tab.path);
          const tenant = tenantName(tab.tenantSlug);
          return (
            <div
              key={tab.key}
              role="tab"
              aria-selected={active}
              aria-label={tab.dirty ? `${title} — ${t('tabs.unsaved')}` : undefined}
              tabIndex={active || (!activeKey && index === 0) ? 0 : -1}
              data-active={active || undefined}
              title={tenant ? `${title} · ${tenant}` : title}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(DRAG_TYPE, tab.key);
                e.dataTransfer.effectAllowed = 'move';
                setDragKey(tab.key);
              }}
              onDragEnd={() => setDragKey(null)}
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes(DRAG_TYPE)) e.preventDefault();
              }}
              onDrop={(e) => onDrop(e, index)}
              onClick={() => !active && api.activate(tab.key)}
              onMouseDown={(e) => e.button === 1 && e.preventDefault()}
              onAuxClick={(e) => {
                if (e.button === 1) {
                  e.preventDefault();
                  requestClose(tab);
                }
              }}
              onKeyDown={(e) => onKeyDown(e, tab, index)}
              className={cn(
                'group/tab relative flex h-32 max-w-280 shrink-0 cursor-pointer items-center gap-8 px-12 py-6 select-none',
                active ? 'z-2 rounded-t-10 bg-panel' : 'rounded-12 bg-tab-idle',
                dragKey === tab.key && 'opacity-50',
                rowFocus,
              )}
            >
              <span className="flex min-w-0 items-center gap-8">
                {tab.icon?.color ? (
                  <IconChip name={tab.icon.name} color={tab.icon.color} size={20} />
                ) : (
                  <NamedIcon name={tab.icon?.name ?? 'file-line'} className={cn('size-18 shrink-0', active ? 'text-fg' : 'text-icon')} />
                )}
                <span className={cn('truncate text-sm', active ? 'font-semibold text-fg' : 'text-fg-secondary group-hover/tab:text-fg-nav')}>
                  {title}
                </span>
              </span>
              {(!active || tab.pinned) && (
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={t(tab.pinned ? 'tabs.unpin' : 'tabs.pin')}
                  aria-pressed={Boolean(tab.pinned)}
                  title={t(tab.pinned ? 'tabs.unpin' : 'tabs.pin')}
                  onClick={(e) => {
                    e.stopPropagation();
                    api.togglePin(tab.key);
                  }}
                  className={cn(
                    'flex size-18 shrink-0 items-center justify-center rounded-4 hover:bg-hover hover:text-fg',
                    tab.pinned ? 'text-fg' : 'text-icon',
                  )}
                >
                  {tab.pinned ? <RiPushpin2Fill aria-hidden className="size-18" /> : <RiPushpin2Line aria-hidden className="size-18" />}
                </button>
              )}
              <button
                type="button"
                tabIndex={-1}
                aria-label={t('tabs.close')}
                onClick={(e) => {
                  e.stopPropagation();
                  requestClose(tab);
                }}
                className="group/close relative flex size-18 shrink-0 items-center justify-center rounded-4 text-icon hover:bg-hover hover:text-fg"
              >
                {tab.dirty ? (
                  <>
                    <span aria-hidden className="size-8 rounded-full bg-accent-2 group-hover/close:hidden" />
                    <RiCloseLine aria-hidden className="hidden size-18 group-hover/close:block" />
                  </>
                ) : (
                  <RiCloseLine aria-hidden className="size-18" />
                )}
              </button>
            </div>
          );
        })}
      </div>

      {edges.left && (
        <button
          type="button"
          aria-label={t('tabs.scrollLeft')}
          onClick={() => scrollBy(-1)}
          className="absolute inset-y-0 left-0 z-3 flex w-32 items-center justify-start bg-linear-to-r from-panel from-50% to-transparent pl-4 text-icon hover:text-fg"
        >
          <RiArrowLeftSLine aria-hidden className="size-18" />
        </button>
      )}
      {edges.right && (
        <button
          type="button"
          aria-label={t('tabs.scrollRight')}
          onClick={() => scrollBy(1)}
          className="absolute inset-y-0 right-0 z-3 flex w-32 items-center justify-end bg-linear-to-l from-panel from-50% to-transparent pr-4 text-icon hover:text-fg"
        >
          <RiArrowRightSLine aria-hidden className="size-18" />
        </button>
      )}

      <AlertDialog.Root open={pendingClose !== null} onOpenChange={(open) => !open && setPendingClose(null)}>
        <AlertDialog.Portal>
          <AlertDialog.Backdrop className="fixed inset-0 z-60 bg-overlay transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0" />
          <AlertDialog.Popup className="fixed top-1/2 left-1/2 z-70 flex w-464 max-w-[calc(100vw-2rem)] -translate-1/2 flex-col rounded-12 border border-line bg-modal outline-none transition-[opacity,scale] duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0">
            <div className="flex gap-12 px-16 py-12">
              <RiErrorWarningLine aria-hidden className="size-24 shrink-0 text-warning" />
              <div className="flex min-w-0 flex-col gap-2">
                <AlertDialog.Title className="text-base font-semibold text-fg">{t('tabs.confirmClose.title')}</AlertDialog.Title>
                <AlertDialog.Description className="text-sm text-fg-secondary">
                  {t('tabs.confirmClose.text', { title: pendingClose ? pendingClose.title || (pendingClose.titleKey ? t(pendingClose.titleKey) : '') : '' })}
                </AlertDialog.Description>
              </div>
            </div>
            <div className="flex justify-end gap-8 border-t border-line px-16 py-12">
              <AlertDialog.Close className={cn('h-32 rounded-8 bg-subtle px-10 text-sm font-semibold text-fg hover:bg-field', rowFocus)}>
                {t('tabs.confirmClose.stay')}
              </AlertDialog.Close>
              <button
                type="button"
                onClick={() => {
                  if (pendingClose) api.close(pendingClose.key);
                  setPendingClose(null);
                }}
                className={cn('h-32 rounded-8 bg-danger-tone px-10 text-sm font-semibold text-white hover:opacity-90', rowFocus)}
              >
                {t('tabs.confirmClose.close')}
              </button>
            </div>
          </AlertDialog.Popup>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
